import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layout } from '@/components/layout';
import SEO from '@/components/shared/SEO';

const PrivacyPolicyPage = () => {
  const lastUpdated = 'April 7, 2026';

  return (
    <Layout>
      <SEO
        title="Privacy Policy"
        description="Portico Systems Privacy Policy — how we collect, use, and protect your personal information."
        canonicalUrl="/privacy"
      />

      <section className="bg-primary-bg text-primary-foreground py-12 md:py-16">
        <div className="container-portico">
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-heading text-4xl md:text-5xl font-bold"
          >
            Privacy Policy
          </motion.h1>
          <p className="mt-3 text-sm opacity-70">Last updated: {lastUpdated}</p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container-portico max-w-3xl">
          <div className="prose prose-gray max-w-none space-y-8 text-foreground/90">

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground mb-3">1. Introduction</h2>
              <p className="leading-relaxed">
                Portico Systems ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website <strong>porticosystems.com</strong> (the "Site") and use our services. By using the Site, you agree to the terms of this policy.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground mb-3">2. Information We Collect</h2>
              <p className="leading-relaxed mb-3">We may collect information about you in a variety of ways:</p>
              <h3 className="font-heading text-base font-semibold text-foreground mb-2">Personal Data</h3>
              <p className="leading-relaxed mb-2">When you voluntarily submit forms on our Site — including contact forms, quote requests, sample requests, consultation scheduling, or project inquiries — we may collect:</p>
              <ul className="list-disc pl-6 space-y-1.5 text-sm">
                <li>Full name</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Company name and job title</li>
                <li>Mailing or shipping address</li>
                <li>Project details (type, size, timeline, budget)</li>
              </ul>

              <h3 className="font-heading text-base font-semibold text-foreground mt-4 mb-2">Automatically Collected Data</h3>
              <p className="leading-relaxed">When you access the Site, we may automatically collect device and usage information such as your IP address, browser type, operating system, pages visited, and time spent on pages.</p>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground mb-3">3. How We Use Your Information</h2>
              <p className="leading-relaxed mb-2">We may use the information we collect for purposes including:</p>
              <ul className="list-disc pl-6 space-y-1.5 text-sm">
                <li>Responding to your inquiries and fulfilling your requests</li>
                <li>Processing sample and quote requests</li>
                <li>Scheduling consultations and service calls</li>
                <li>Sending you relevant product information and marketing communications (with your consent)</li>
                <li>Improving and optimizing our website and services</li>
                <li>Complying with legal obligations</li>
              </ul>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground mb-3">4. Sharing Your Information</h2>
              <p className="leading-relaxed">
                We do not sell, trade, or rent your personal information to third parties. We may share data with trusted service providers who assist in operating our website, conducting business, or servicing you — provided they agree to keep this information confidential. We may also release information when required by law or to protect our rights.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground mb-3">5. Cookies &amp; Tracking Technologies</h2>
              <p className="leading-relaxed">
                We may use cookies, web beacons, and similar tracking technologies to enhance your experience on our Site. Cookies are small data files stored on your device that help us remember preferences and understand usage patterns. You can manage cookie preferences through your browser settings. Disabling cookies may affect certain features of the Site.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground mb-3">6. Data Security</h2>
              <p className="leading-relaxed">
                We implement reasonable administrative, technical, and physical safeguards to protect your personal information from unauthorized access, disclosure, alteration, and destruction. However, no method of transmission over the Internet or electronic storage is completely secure, and we cannot guarantee absolute security.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground mb-3">7. Data Retention</h2>
              <p className="leading-relaxed">
                We retain personal information only for as long as necessary to fulfill the purposes for which it was collected, comply with legal obligations, resolve disputes, and enforce our agreements.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground mb-3">8. Your Rights</h2>
              <p className="leading-relaxed mb-2">Depending on your location, you may have certain rights regarding your personal information, including the right to:</p>
              <ul className="list-disc pl-6 space-y-1.5 text-sm">
                <li>Access and receive a copy of the personal data we hold about you</li>
                <li>Request correction or deletion of your personal data</li>
                <li>Opt out of marketing communications at any time</li>
                <li>Withdraw consent where processing is based on consent</li>
              </ul>
              <p className="leading-relaxed mt-3">
                To exercise any of these rights, please contact us at{' '}
                <a href="mailto:privacy@porticosystems.com" className="text-primary hover:underline">privacy@porticosystems.com</a>.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground mb-3">9. Third-Party Links</h2>
              <p className="leading-relaxed">
                Our Site may contain links to third-party websites. We are not responsible for the privacy practices or content of those sites. We encourage you to review the privacy policies of any third-party sites you visit.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground mb-3">10. Children's Privacy</h2>
              <p className="leading-relaxed">
                Our Site is not directed at individuals under the age of 13. We do not knowingly collect personal information from children. If we learn that we have collected data from a child under 13, we will take steps to delete it promptly.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground mb-3">11. Changes to This Policy</h2>
              <p className="leading-relaxed">
                We reserve the right to update this Privacy Policy at any time. Changes will be posted on this page with a revised "Last Updated" date. Your continued use of the Site after changes are posted constitutes your acceptance of the updated policy.
              </p>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground mb-3">12. Contact Us</h2>
              <p className="leading-relaxed">
                If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="mt-3 bg-muted rounded-xl p-5 text-sm space-y-1">
                <p className="font-semibold">Portico Systems</p>
                <p>Email: <a href="mailto:privacy@porticosystems.com" className="text-primary hover:underline">privacy@porticosystems.com</a></p>
                <p>Phone: <a href="tel:1-864-527-3148" className="text-primary hover:underline">1-864-527-3148</a></p>
              </div>
            </div>

          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PrivacyPolicyPage;
